import { startTransition, useMemo } from "react"
import type { FC, ReactElement } from "react"

import isEmpty from "lodash-es/isEmpty"
import { useBoolean, useInterval, useUpdateEffect } from "react-use"

import { Accordion } from "@/3514/components/shared/accordion/accordion.component"
import {
    EAccordionBordersVariant,
    EAccordionColorVariant,
    type TAccordionRenderFunction
} from "@/3514/components/shared/accordion/accordion.types"
import { LiveMark } from "@/3514/components/shared/marks/live-now/live-mark.component"
import { ESessionInfoColorSchema as EColorSchema } from "@/3514/components/shared/session-info/session-info.types"
import { type TSessionTimeState, useSessionTimeState } from "@/3514/hooks"
import { seconds } from "@/3514/utils/time.utils"

import Body from "./session-info.component.body"
import Header from "./session-info.component.header"
import classes from "./session-info.styles"
import type { TSessionInfoProps as TProps } from "./session-info.types"
import utils from "./session-info.utils"

const SessionInfo: FC<TProps> = ({
    tag: Tag,
    viewerRole,
    isExpandable,
    isDefaultExpanded,
    withLiveChecker,
    onLiveSessionStarted,
    onLiveSessionPassed,
    onAttendeesListHover,
    getColorSchema,
    sessionsQueryKey,
    data,
    renderers
}: TProps): ReactElement => {
    const timeState: TSessionTimeState = useSessionTimeState({
        startDate: data.session.startDate,
        durationInMins: data.session.duration,
        isEnabled: withLiveChecker
    })

    const [isSwappable, setIsSwappable]: ReturnType<typeof useBoolean> = useBoolean(
        data.session.isSwappable ? utils.getIsSwapSessionVisibleBasedOnDate(data.session.startDate) : false
    )

    useInterval(
        (): void => {
            if (data.session.isSwappable)
                startTransition((): void =>
                    setIsSwappable(utils.getIsSwapSessionVisibleBasedOnDate(data.session.startDate))
                )
        },
        data.session.isSwappable ? seconds(5) : null
    )

    useUpdateEffect((): void => {
        if (timeState.isLive && !timeState.wasLive) void onLiveSessionStarted()
    }, [onLiveSessionStarted, timeState.isLive, timeState.wasLive])

    useUpdateEffect((): void => {
        if (timeState.wasLive) void onLiveSessionPassed()
    }, [onLiveSessionPassed, timeState.wasLive])

    const stateData: TProps["data"] = useMemo<TProps["data"]>(
        (): TProps["data"] => ({
            ...data,
            session: { ...data.session, isSwappable }
        }),
        [data, isSwappable]
    )

    const { colorSchema, accordionSchema } = ((): {
        colorSchema: EColorSchema
        accordionSchema: EAccordionColorVariant
    } => {
        const colorSchema: EColorSchema = getColorSchema({
            isLive: timeState.isLive,
            isCancelled: data.session.isCancelled,
            isMissed: !isEmpty(data.session.participants) && data.session.isMissed
        })

        const accordionSchema: EAccordionColorVariant = ((): EAccordionColorVariant => {
            switch (colorSchema) {
                case EColorSchema.AccentBorderWhiteBg:
                    return EAccordionColorVariant.Accent
                case EColorSchema.TransparentBorderBlueBg:
                    return EAccordionColorVariant.WhiteMuted
                case EColorSchema.OrangeBorderWhiteBg:
                    return EAccordionColorVariant.Orange
                case EColorSchema.BlueBorderWhiteBg:
                default:
                    return EAccordionColorVariant.White
            }
        })()

        return { colorSchema, accordionSchema }
    })()

    const renderHeader: TAccordionRenderFunction = (isExpanded: boolean): ReactElement => (
        <Header
            data={stateData}
            isAccordionExpanded={isExpanded}
            renderHeaderAvatar={renderers.renderHeaderAvatar}
            renderHeaderNameToolip={renderers.renderHeaderNameToolip}
        />
    )

    const renderBody: TAccordionRenderFunction = (isExpanded: boolean): ReactElement => (
        <Body
            viewerRole={viewerRole}
            colorSchema={colorSchema}
            data={stateData}
            onAttendeesListHover={onAttendeesListHover}
            sessionsQueryKey={sessionsQueryKey}
            sessionTimeState={timeState}
            isAccordionExpanded={isExpanded}
            isAccordionExpandable={isExpandable}
            {...renderers}
        />
    )

    return (
        <Tag className={classes.container({ viewerRole })}>
            <LiveMark isVisible={timeState.isLive} />

            <Accordion
                isExpandable={isExpandable}
                isDefaultExpanded={isDefaultExpanded}
                renderHeader={renderHeader}
                renderBody={renderBody}
                renderIcons={[
                    renderers?.renderAttendanceModalOpener,
                    renderers.renderCancelledSessionModalOpener,
                    (_isExpanded: boolean): ReactElement =>
                        renderers.renderHeaderDropdown({
                            data,
                            viewerRole,
                            sessionsQueryKey,
                            sessionTimeState: timeState
                        }),
                    renderers.renderPostSessionModalOpener,
                    renderers.renderHeaderChevron
                ]}
                colorVariant={accordionSchema}
                bordersVariant={EAccordionBordersVariant.ThinBordered}
                headerBorderRadius={{
                    collapsed: { tl: 10, tr: 10, br: 10, bl: 10 },
                    expanded: { tl: 10, tr: 10, br: 0, bl: 0 }
                }}
            />
        </Tag>
    )
}

SessionInfo.displayName = "SessionInfo"

export { SessionInfo }
